import { useMemo } from 'react';
import { type IFilterButtonsResult } from '@/creditflowresearch/ui/common/hooks/filters/useCreateFilterButtons';
import { useRegionFilters } from '@/creditflowresearch/ui/common/hooks/regions/useRegionFilters';
import { useMarketFilters } from '@/creditflowresearch/ui/common/hooks/markets/useMarketFilters';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import Preferences from '@/creditflowresearch/domain/Preferences/Preferences';
import MenuRegionResolver, {
  MenuPanelKind,
  menuPanelKindFromProductId,
} from '@/creditflowresearch/domain/Preferences/MenuRegionResolver';
import { createRegionOrMarketAccessPredicate } from '@/creditflowresearch/application/Preferences/selectAccessibleMappedIds';

const CORPORATE_PANEL_KINDS: MenuPanelKind[] = [MenuPanelKind.HYC, MenuPanelKind.IGC];

export const usePanelContentFilter = (panelId: string): IFilterButtonsResult => {
  const { preferences, plan, regions: accessibleRegionsIds } = useUserPlanContext();

  const panelKind = menuPanelKindFromProductId(panelId);
  const isCorporatePanel = !!panelKind && CORPORATE_PANEL_KINDS.includes(panelKind);

  const preselectedIds = useMemo<string[] | undefined>(() => {
    if (!panelKind || !preferences) {
      return undefined;
    }

    const preselectedId = MenuRegionResolver.resolveForPanel(
      new Preferences(preferences),
      panelKind,
      createRegionOrMarketAccessPredicate({
        plan,
        accessibleRegionsIds: accessibleRegionsIds || [],
      }),
    );

    return preselectedId ? [preselectedId] : undefined;
  }, [panelKind, preferences, plan, accessibleRegionsIds]);

  const regionFilters = useRegionFilters(isCorporatePanel ? undefined : preselectedIds);
  const marketFilters = useMarketFilters(isCorporatePanel ? preselectedIds : undefined);

  return isCorporatePanel ? marketFilters : regionFilters;
};
